import { v4 as uuid } from "uuid";
import { normalizeMediaUrl, normalizeProduct } from "../../utils/media";
import {
  ADD_TO_CART,
  DECREASE_QUANTITY,
  DELETE_FROM_CART,
  DELETE_ALL_FROM_CART,
  SET_CART_FROM_BACKEND,
  CART_LOADING,
  CART_ERROR,
  UPDATE_CART_ITEM,
  UPSERT_CART_ITEM
} from "../actions/cartActions";

// Initial state now includes loading and error states
const initState = {
  items: [],
  loading: false,
  error: null,
  totals: {
    subtotal: '0.00',
    item_count: 0,
    total_items: 0,
    currency: 'INR'
  }
};
const DEFAULT_PRODUCT_IMAGE = '/assets/img/product/hm6-pro-1.jpg';

const normalizeCartItem = (item) => {
  const normalizedProduct = item.product ? normalizeProduct(item.product) : item.product;
  const productId = typeof normalizedProduct === 'object' && normalizedProduct !== null
    ? normalizedProduct.id
    : item.product;
  const priceValue = normalizedProduct?.price || item.product_price || 0;
  const imageCandidate = normalizedProduct?.images?.[0]?.image_url ||
    normalizedProduct?.images?.[0]?.image ||
    normalizedProduct?.highlight_image?.image_url ||
    normalizedProduct?.highlight_image?.image ||
    item.image;
  const normalizedImage = normalizeMediaUrl(imageCandidate);

  return {
    ...item,
    cartItemId: item.id,
    id: productId || item.product_id || item.id,
    name: normalizedProduct?.name || item.product_name,
    slug: normalizedProduct?.slug,
    price: parseFloat(priceValue || 0),
    image: normalizedImage ? [normalizedImage] : [DEFAULT_PRODUCT_IMAGE],
    brand: normalizedProduct?.brand,
    quantity: item.quantity,
    subtotal: parseFloat(item.subtotal || (priceValue || 0) * (item.quantity || 1)),
    product: normalizedProduct
  };
};

const normalizeId = (value) => (value === undefined || value === null ? "" : String(value));

const cartReducer = (state = initState, action) => {
  // Handle both old array format and new object format
  const cartItems = Array.isArray(state) ? state : (state.items || []);
  const product = action.payload;

  switch (action.type) {
    case SET_CART_FROM_BACKEND:
      // Transform backend cart items to match expected format
      const backendItems = (action.payload.items || []).map(normalizeCartItem);

      const totals = action.payload.totals || {};
      const computedItemCount = backendItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const itemCount = totals.item_count ?? totals.total_items ?? computedItemCount;
      const normalizedTotals = {
        ...totals,
        item_count: itemCount,
        total_items: totals.total_items ?? itemCount,
        subtotal: totals.subtotal ?? '0.00',
        currency: totals.currency ?? 'INR'
      };

      return {
        items: backendItems,
        loading: false,
        error: null,
        totals: normalizedTotals
      };
    case UPSERT_CART_ITEM: {
      const normalizedItem = normalizeCartItem(action.payload);
      const normalizedCartItemId = normalizeId(normalizedItem.cartItemId);
      const normalizedProductId = normalizeId(normalizedItem.id);
      const isSameItem = (item) =>
        (normalizedCartItemId && normalizeId(item.cartItemId) === normalizedCartItemId) ||
        (normalizedProductId && normalizeId(item.id) === normalizedProductId);
      const existingIndex = cartItems.findIndex(item => isSameItem(item));
      const updatedItems = existingIndex >= 0
        ? cartItems.map(item =>
            isSameItem(item)
              ? { ...item, ...normalizedItem, quantity: normalizedItem.quantity }
              : item
          )
        : [...cartItems, normalizedItem];
      const computedItemCount = updatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
      const computedSubtotal = updatedItems.reduce((sum, item) => sum + (item.subtotal || 0), 0);
      const normalizedTotals = {
        ...state.totals,
        item_count: computedItemCount,
        total_items: computedItemCount,
        subtotal: computedSubtotal.toFixed(2),
        currency: state.totals?.currency || 'INR'
      };

      return {
        ...state,
        items: updatedItems,
        loading: false,
        error: null,
        totals: normalizedTotals
      };
    }

    case CART_LOADING:
      return {
        ...state,
        items: Array.isArray(state) ? state : (state.items || []),
        loading: action.payload,
        error: null
      };

    case CART_ERROR:
      return {
        ...state,
        items: Array.isArray(state) ? state : (state.items || []),
        loading: false,
        error: action.payload
      };

    case UPDATE_CART_ITEM:
      return {
        ...state,
        items: cartItems.map(item =>
          normalizeId(item.cartItemId) === normalizeId(product.cartItemId)
            ? { ...item, quantity: product.quantity }
            : item
        )
      };

    case ADD_TO_CART:
      // Legacy: for non-variant products
      if (product.variation === undefined) {
        const cartItem = cartItems.filter(item => normalizeId(item.id) === normalizeId(product.id))[0];
        if (cartItem === undefined) {
          const newItems = [
            ...cartItems,
            {
              ...product,
              quantity: product.quantity ? product.quantity : 1,
              cartItemId: uuid()
            }
          ];
          return Array.isArray(state) ? newItems : { ...state, items: newItems };
        } else {
          const updatedItems = cartItems.map(item =>
            item.cartItemId === cartItem.cartItemId
              ? {
                  ...item,
                  quantity: product.quantity
                    ? item.quantity + product.quantity
                    : item.quantity + 1
                }
              : item
          );
          return Array.isArray(state) ? updatedItems : { ...state, items: updatedItems };
        }
      } else {
        // Legacy: for variant products
        const cartItem = cartItems.filter(
          item =>
            normalizeId(item.id) === normalizeId(product.id) &&
            product.selectedProductColor &&
            product.selectedProductColor === item.selectedProductColor &&
            product.selectedProductSize &&
            product.selectedProductSize === item.selectedProductSize &&
            (product.cartItemId ? normalizeId(product.cartItemId) === normalizeId(item.cartItemId) : true)
        )[0];

        if (cartItem === undefined) {
          const newItems = [
            ...cartItems,
            {
              ...product,
              quantity: product.quantity ? product.quantity : 1,
              cartItemId: uuid()
            }
          ];
          return Array.isArray(state) ? newItems : { ...state, items: newItems };
        } else if (
          cartItem !== undefined &&
          (cartItem.selectedProductColor !== product.selectedProductColor ||
            cartItem.selectedProductSize !== product.selectedProductSize)
        ) {
          const newItems = [
            ...cartItems,
            {
              ...product,
              quantity: product.quantity ? product.quantity : 1,
              cartItemId: uuid()
            }
          ];
          return Array.isArray(state) ? newItems : { ...state, items: newItems };
        } else {
          const updatedItems = cartItems.map(item =>
            item.cartItemId === cartItem.cartItemId
              ? {
                  ...item,
                  quantity: product.quantity
                    ? item.quantity + product.quantity
                    : item.quantity + 1,
                  selectedProductColor: product.selectedProductColor,
                  selectedProductSize: product.selectedProductSize
                }
              : item
          );
          return Array.isArray(state) ? updatedItems : { ...state, items: updatedItems };
        }
      }

    case DECREASE_QUANTITY:
      if (product.quantity === 1) {
        const remainingItems = cartItems.filter(
          cartItem => normalizeId(cartItem.cartItemId) !== normalizeId(product.cartItemId)
        );
        return Array.isArray(state) ? remainingItems : { ...state, items: remainingItems };
      } else {
        const updatedItems = cartItems.map(item =>
          normalizeId(item.cartItemId) === normalizeId(product.cartItemId)
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
        return Array.isArray(state) ? updatedItems : { ...state, items: updatedItems };
      }

    case DELETE_FROM_CART:
      const filteredItems = cartItems.filter(
        cartItem => normalizeId(cartItem.cartItemId) !== normalizeId(product.cartItemId)
      );
      return Array.isArray(state) ? filteredItems : { ...state, items: filteredItems };

    case DELETE_ALL_FROM_CART:
      return Array.isArray(state) ? [] : { ...state, items: [], totals: { subtotal: '0.00', item_count: 0, total_items: 0, currency: 'INR' } };

    default:
      // Maintain backward compatibility
      return Array.isArray(state) ? state : (state.items ? state : { items: state, loading: false, error: null, totals: initState.totals });
  }
};

export default cartReducer;
